import { z } from "zod";
import { companySchema } from "./company";
import { companyFinancialsSchema } from "./companyFinancials";

/**
 * The frozen snapshot stored in shareable_reports.snapshot_json. Mirrors
 * what the Sidequester POST companies/{id}/share-report endpoint constructs.
 *
 * Note: company has user_id stripped server-side.
 */
export const reportSnapshotSchema = z.object({
  company: companySchema.omit({ user_id: true }).passthrough(),
  financials: z.array(companyFinancialsSchema),
  generated_at: z.union([z.string(), z.number()]),
});
export type ReportSnapshot = z.infer<typeof reportSnapshotSchema>;

/**
 * Brand metadata returned by the public share-reports endpoint, so the
 * SPA can skin a shared link to match the originating site (gocfo, etc.).
 */
export const reportSiteBrandSchema = z
  .object({
    id: z.number().int(),
    domain: z.string(),
    name: z.string().nullable().optional(),
    primary_color: z.string().nullable().optional(),
    secondary_color: z.string().nullable().optional(),
    accent_color: z.string().nullable().optional(),
    logo_url: z.string().nullable().optional(),
    favicon_url: z.string().nullable().optional(),
    cta_text: z.string().nullable().optional(),
    cta_url: z.string().nullable().optional(),
    footer_tagline: z.string().nullable().optional(),
  })
  .nullable();
export type ReportSiteBrand = z.infer<typeof reportSiteBrandSchema>;

export const createShareReportResponseSchema = z.object({
  token: z.string().min(1),
  expires_at: z.union([z.string(), z.number()]),
  source_site_id: z.number().int().nullable().optional(),
});
export type CreateShareReportResponse = z.infer<typeof createShareReportResponseSchema>;

export const publicShareReportSchema = z.object({
  snapshot: reportSnapshotSchema,
  site: reportSiteBrandSchema,
  view_count: z.number(),
  expires_at: z.union([z.string(), z.number()]),
  created_at: z.union([z.string(), z.number()]),
});
export type PublicShareReport = z.infer<typeof publicShareReportSchema>;
